// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import type { TyLead } from "@/types";
import Icon from "@/UI/atoms/Icon";
import SelectInput from "@/UI/inputs/SelectInput";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
export type TyCloseLeadDetails = { lead: TyLead; onDone: () => void };

type Props = { onClose: () => void; details?: unknown };

/**
 * Taking a duplicate off the board — or putting one back. One popup for both, because `closed` is
 * one field: the reason **is** the flag.
 *
 * The hint is doing real work here. "Close" and "lost" look like the same action to somebody
 * clearing their list, and the difference is the only thing keeping the loss report honest: a
 * company that tidies its board should not read as a company that loses deals.
 */
export default function CloseLeadPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { lead, onDone } = details as TyCloseLeadDetails;

    const isReopening = !!lead.closed;
    const [reason, setReason] = useState<string>(constants.leadCloseReasons[0]);

    const { loading, error, succeededAt, invoke } = useFetch(`/crm/leads/${lead._id}/closure`, { method: "PATCH" }, false);

    useEffect(() => {
        if (!succeededAt) return;
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = () => invoke({ method: "PATCH", data: { closed: isReopening ? null : reason } });

    return (
        <section className="side-form">
            <header className="side-form__header">
                <h2 className="side-form__title">{isReopening ? t.crm.reopenLead : t.crm.closeLead}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                {!isReopening && (
                    <>
                        <SelectInput
                            value={reason}
                            onChange={setReason}
                            options={constants.leadCloseReasons.map((item) => ({ value: item, label: t.crm.closeReasons[item] }))}
                            label={t.crm.fields.closeReason}
                            disabled={loading}
                        />
                        <p className="close-lead__hint">{t.crm.closeHint}</p>
                    </>
                )}

                {isReopening && <p className="close-lead__hint">{t.crm.closeReasons[lead.closed!]}</p>}

                {error && <p className="side-form__error">{error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button type="button" className="side-form__submit" onClick={handleSubmit} disabled={loading}>
                    {loading ? t.common.loading : isReopening ? t.crm.reopenLead : t.crm.closeLead}
                </button>
            </footer>
        </section>
    );
}
